


































import Vue from 'vue';
import {
  Component, Inject, Watch, Prop,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import {
  Alert, NoticeClass, AuthStore,
} from '@kasasa/fbase-components/lib';
import { DataTableHeader } from 'vuetify';
import { InControlDetailsResource, InControlDetail } from '@/api/incontroldetails';
import { CycleResource, Cycle } from '@/api/cycles';
import { Account } from '@/api/consumeraccountdetails';

interface InControlDetailTableEntry {
  cycleId: number,
  cycleName: string,
  statementEndDate: string,
  inControlDetail: InControlDetail,
  sortDate: number
}

@Component({
  computed: mapState([
    'fiId',
  ]),
  components: {

  },
})
export default class InControlDetailTable extends Vue {
  @Prop() account!: Account;

  @Prop() cycles!: Cycle[];

  detailItems: InControlDetailTableEntry[] = [];

  fiId!: string;

  loading: boolean = true;

  loadingMore: boolean = false;

  nextCycleIndex: number = 0;

  headers: DataTableHeader[] = []

  @Inject('InControlDetailsResource')
  inControlDetailsResource!: InControlDetailsResource;

  mounted() {
    this.loadDetails();
  }

  @Watch('account')
  async loadDetails() {
    this.loading = true;
    this.detailItems = [];
    this.headers = [];
    if (this.fiId && this.account && this.cycles.length > 0) {
      this.nextCycleIndex = this.cycles.length - 1;
      const currentDetails: InControlDetailTableEntry | null = await
      this.loadDetailsForCycle(this.cycles[this.nextCycleIndex--]);
      if (currentDetails) {
        this.detailItems.push(currentDetails);
      }

      if (this.nextCycleIndex > -1) {
        const previousDetails = await this.loadDetailsForCycle(this.cycles[this.nextCycleIndex--]);
        if (previousDetails) {
          this.detailItems.push(previousDetails);
        }
      }
      if (this.detailItems.length > 1) {
        this.detailItems.sort(this.compareQualificationCycles);
        this.detailItems[0].cycleName = 'Current';
        this.detailItems[1].cycleName = 'Previous';
      } else if (this.detailItems.length === 1) {
        this.detailItems[0].cycleName = 'Current';
      }
    }
    this.loading = false;
  }

  async loadMore() {
    this.loadingMore = true;
    const promises: Promise<InControlDetailTableEntry | null>[] = [];
    for (let i = 0; i < 10 && this.nextCycleIndex > -1; i++) {
      promises.push(this.loadDetailsForCycle(this.cycles[this.nextCycleIndex--]));
    }
    // Make sure results are sorted by end date before adding to table
    await Promise.all(promises).then((values: (InControlDetailTableEntry | null)[]) => {
      values.sort(this.compareQualificationCycles);
      const parent: InControlDetailTable = this;
      values.forEach((element) => {
        if (element) {
          parent.detailItems.push(element);
        }
      });
    });
    this.loadingMore = false;
  }

  // The order of this sort is reversed so the result is in descending order
  compareQualificationCycles = (control1: InControlDetailTableEntry | null, control2: InControlDetailTableEntry | null): number => {
    if (control1 == null || control2 == null) {
      return 0;
    } if (control1.sortDate > control2.sortDate) {
      return -1;
    } if (control1.sortDate < control2.sortDate) {
      return 1;
    }
    return 0;
  }

  async loadDetailsForCycle(cycle: Cycle): Promise<InControlDetailTableEntry | null> {
    try {
      const detail: InControlDetail | null = await this.inControlDetailsResource.getInControlDetail(
        this.fiId, cycle.id, this.account.externalProductMappingId, this.account.accountKasasaKey,
      );

      if (detail != null) {
        const entry: InControlDetailTableEntry = InControlDetailTable.buildTableEntry(detail, cycle);
        if (this.headers.length === 0) {
          this.buildHeaders(detail);
        }
        return entry;
      }
      return null;
    } catch (error) {
      this.showErrorMessage(error);
      throw error;
    }
  }

  static buildTableEntry(detail: InControlDetail, cycle: Cycle): InControlDetailTableEntry {
    return {
      cycleId: cycle.id,
      cycleName: '',
      statementEndDate: new Date(cycle.statementEndDate).toLocaleDateString('en-US', { timeZone: 'UTC' }),
      sortDate: new Date(cycle.qualificationEndDate).getTime(),
      inControlDetail: detail,
    };
  }

  buildHeaders(details: InControlDetail) {
    this.headers.push({ text: 'Rewards Cycle', value: 'cycleName' });
    this.headers.push({ text: 'Statement End Date', value: 'sortDate' });

    if (details.estatementReductionAmount !== null) {
      this.headers.push({ text: 'E-Statement Reduction', value: 'inControlDetail.estatementReductionAmount' });
    }

    if (details.accountOpen !== null) {
      this.headers.push({ text: 'Account Opening Reduction', value: 'inControlDetail.accountOpen' });
    }

    if (details.iblogin !== null) {
      this.headers.push({ text: 'IbLogin Reduction', value: 'inControlDetail.iblogin' });
    }

    if (details.perPos !== null) {
      this.headers.push({ text: 'Per POS Reduction', value: 'inControlDetail.perPos' });
    }

    this.headers.push({ text: 'Net Service Fee Amount', value: 'inControlDetail.netServiceFee' });
  }

  getTableValue = (item: InControlDetailTableEntry, header: any): String => {
    let value: String = '';

    if (header.value) {
      if (header.value === 'cycleName') {
        value = item.cycleName;
      } else if (header.value === 'sortDate') {
        value = item.statementEndDate;
      } else if (header.value === 'inControlDetail.estatementReductionAmount') {
        value = `$${item.inControlDetail.estatementReductionAmount
          ? Number(item.inControlDetail.estatementReductionAmount).toFixed(2) : '0.00'}`;
      } else if (header.value === 'inControlDetail.accountOpen') {
        value = `$${item.inControlDetail.accountOpen ? Number(item.inControlDetail.accountOpen).toFixed(2) : '0.00'}`;
      } else if (header.value === 'inControlDetail.iblogin') {
        value = `$${item.inControlDetail.iblogin ? Number(item.inControlDetail.iblogin).toFixed(2) : '0.00'}`;
      } else if (header.value === 'inControlDetail.perPos') {
        value = `$${item.inControlDetail.perPos ? Number(item.inControlDetail.perPos).toFixed(2) : '0.00'}`;
      } else if (header.value === 'inControlDetail.netServiceFee') {
        value = `$${item.inControlDetail.netServiceFee
          ? Number(item.inControlDetail.netServiceFee).toFixed(2) : '0.00'}`;
      }
    }
    return value;
  };

  showErrorMessage(error: string) {
    const al = new Alert(error, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
